import React from 'react';
import { ModuleConfig } from '../../types/ModuleConfig';

const MeetingsList = React.lazy(() => import('./MeetingsList'));
const NewMeetingSummary = React.lazy(() => import('./NewMeetingSummary'));
const MeetingDetails = React.lazy(() => import('./MeetingDetails'));

const meetingSummarizerModule: ModuleConfig = {
  // 🏷️ MODULE IDENTITY
  id: 'meeting-summarizer',
  name: 'Meeting Summarizer',
  description: 'Record, transcribe, and summarize meetings with AI-powered insights',
  version: '1.0.0',
  enabled: true,
  category: 'automation',
  order: 10,
  icon: '🎙️',
  color: '#805AD5',

  // 🛣️ ROUTES
  routes: [
    {
      path: '/meeting-summarizer',
      component: MeetingsList,
      permissions: ['MEETING_SUMMARIZER_ADMIN', 'MEETING_SUMMARIZER_USER', 'ADMIN', 'MANAGER']
    },
    {
      path: '/meeting-summarizer/new',
      component: NewMeetingSummary,
      permissions: ['MEETING_SUMMARIZER_ADMIN', 'ADMIN', 'MANAGER']
    },
    {
      path: '/meeting-summarizer/:id',
      component: MeetingDetails,
      permissions: ['MEETING_SUMMARIZER_ADMIN', 'MEETING_SUMMARIZER_USER', 'ADMIN', 'MANAGER']
    },
  ],

  // 🧭 NAVIGATION
  navigation: [
    {
      label: 'All Meetings',
      path: '/meeting-summarizer',
      icon: '📋',
      permissions: ['MEETING_SUMMARIZER_ADMIN', 'MEETING_SUMMARIZER_USER', 'ADMIN', 'MANAGER']
    },
    {
      label: 'New Meeting',
      path: '/meeting-summarizer/new',
      icon: '➕',
      permissions: ['MEETING_SUMMARIZER_ADMIN', 'ADMIN', 'MANAGER']
    },
  ],

  // 🔒 PERMISSIONS
  permissions: {
    view: ['MEETING_SUMMARIZER_ADMIN', 'MEETING_SUMMARIZER_USER', 'ADMIN', 'MANAGER'],
    create: ['MEETING_SUMMARIZER_ADMIN', 'ADMIN', 'MANAGER'],
    edit: ['MEETING_SUMMARIZER_ADMIN', 'ADMIN', 'MANAGER'],
    delete: ['MEETING_SUMMARIZER_ADMIN', 'ADMIN']
  },

  // ⚡ QUICK ACTIONS
  quickActions: [
    {
      label: 'Record Meeting',
      path: '/meeting-summarizer/new',
      icon: '🎙️',
      description: 'Start recording a new meeting'
    }
  ],

    // 📝 CHANGELOG
    changelog: [
        {
            version: "1.0.0",
            releaseDate: "2025-11-22",
            title: "Stable Release - Production Ready",
            features: [
                "✅ Production-ready AI meeting summarization",
                "✅ Complete meeting recording and transcription",
                "✅ Team member management for meetings",
                "✅ Project integration for action items",
                "✅ IPFS storage for meeting recordings",
                "✅ Advanced transcription services",
                "✅ Meeting title editing and organization"
            ],
            notes: "Official stable release with comprehensive AI-powered meeting management."
        },
        {
            version: "0.6.0",
            releaseDate: "2024-10-31",
            title: "IPFS Server Integration",
            features: [
                "✨ Added own IPFS server support (9 lines)",
                "✨ Enhanced media storage capabilities",
                "✨ Better control over file hosting"
            ],
            notes: "Self-hosted IPFS server integration for meeting recordings."
        },
        {
            version: "0.5.0",
            releaseDate: "2024-10-02",
            title: "Transcription Module Integration",
            features: [
                "✨ Enhanced MeetingDetails with transcription support (34 lines)",
                "✨ Improved meeting content processing",
                "✨ Better transcription accuracy and formatting"
            ],
            notes: "Advanced transcription module integration for better meeting summaries."
        },
        {
            version: "0.4.0",
            releaseDate: "2024-09-21",
            title: "Navigation & Pagination",
            features: [
                "✨ Added enhanced navigation with improved routing (6 lines)",
                "✨ Implemented pagination enhancements (19 insertions)",
                "✨ Better meeting list filtering (10 lines)",
                "✨ Improved user experience across all pages"
            ],
            notes: "Navigation and pagination improvements for better meeting discovery."
        },
        {
            version: "0.3.0",
            releaseDate: "2024-09-20",
            title: "Theme System Migration",
            features: [
                "✨ Full light/dark mode support across all meeting components",
                "✨ Enhanced AddMemberModal with theme awareness (50 lines)",
                "✨ Updated AddToProjectModal color modes (18 lines)",
                "✨ Improved MeetingDetails themes (44 lines)",
                "✨ Refined MeetingsList design (20 lines)",
                "✨ Updated NewMeetingSummary styling (50 lines)",
                "✨ Consistent visual design system"
            ],
            notes: "Complete theme system implementation for better user experience."
        },
        {
            version: "0.2.0",
            releaseDate: "2023-08-24",
            title: "Enhanced Meeting Management",
            features: [
                "✨ Created AddMemberModal component (242 lines, enhanced to 289 lines)",
                "✨ Enhanced MeetingDetails with member management (1,213 insertions)",
                "✨ Implemented meeting title editing (161 lines)",
                "✨ Created AddToProjectModal for task integration (262 lines)",
                "✨ Added delete meeting functionality (49 lines)",
                "✨ Enhanced NewMeetingSummary with better workflow (202 insertions)"
            ],
            notes: "Major feature expansion with team collaboration and project integration."
        },
        {
            version: "0.1.0",
            releaseDate: "2023-08-23",
            title: "Initial Meeting Summarizer System",
            features: [
                "✅ Created MeetingDetails component (532 lines)",
                "✅ Created MeetingsList component (289 lines)",
                "✅ Created NewMeetingSummary component (569 lines)",
                "✅ AI-powered meeting summarization",
                "✅ Recording and transcription capabilities",
                "✅ Basic meeting organization",
                "✅ Module configuration and routing",
                "✅ Role-based permissions"
            ],
            notes: "Initial implementation of AI meeting summarizer with 1,464 lines of code."
        }
    ],

    // 🗺️ ROADMAP
    roadmap: []

};

export default meetingSummarizerModule;